/* eslint-disable react/no-unused-prop-types */
import { FC, useMemo, useRef, useState } from 'react';
import { Center, Stack } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import { Checkbox } from 'components/_core/form';
import SunshineDropdown from 'components/_core/SunshineDropdown';
import { logEvent } from 'features/Analytics/analytics';
import { CreateClientContactMutation } from 'features/Work/pages/Clients/hooks/useCreateClientContact/graphql/createClientContact.gql';
import { useGetClient } from 'features/Work/pages/Clients/hooks/useGetClient';

import AddClientContactModal from './AddClientContactModal';
import locales from './locales';

interface SelectContactsToSendToProps {
  checkboxId: string;
  clientId: string;
  checked: boolean;
  onClickAddOption?: () => void;
  onChangeCheckbox: (value: boolean) => void;
  onChangeSelect: (value: string[]) => void;
  requiredErrorMessage?: string;
  selectValue: string[];
}

const SelectContactsToSendTo: FC<SelectContactsToSendToProps> = ({
  checkboxId,
  checked,
  clientId,
  onChangeCheckbox,
  onChangeSelect,
  onClickAddOption,
  selectValue,
}) => {
  const selectElementRef = useRef<HTMLSelectElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { client, loading } = useGetClient(clientId);

  const contacts = client?.contacts;
  const isPrivateIndividual = client?.type === 'PRIVATE_INDIVIDUAL';
  const isDisabled = loading || !client || isPrivateIndividual;

  const selectNewContact = (
    newContact: CreateClientContactMutation['createClientContact']['clientContact'],
  ) => {
    if (selectElementRef && selectElementRef.current) {
      selectElementRef.current.focus();
    }

    if (newContact) {
      onChangeSelect([...selectValue, newContact.clientContactId]);
    }
  };

  const contactsOptions = useMemo(() => {
    if (!contacts) {
      return [];
    }

    return contacts.map((contact) => ({
      label: contact?.email,
      value: contact?.clientContactId,
    }));
  }, [contacts]);

  const contactSelected = selectValue.map(
    (value) =>
      contactsOptions.find((option) => option.value === value)?.label ?? '',
  );

  const inputValue = useMemo(() => {
    if (contactSelected.length === 0) {
      return '';
    }

    if (contactSelected.length === 1) {
      return contactSelected[0];
    }

    return `${contactSelected[0]} (+${contactSelected.length - 1})`;
  }, [contactSelected]);

  return (
    <Stack spacing="space-16">
      <Form.Field label={locales.sendTo}>
        <SunshineDropdown>
          <SunshineDropdown.Trigger width="full">
            <Form.Input
              isDisabled={isDisabled}
              placeholder={locales.chooseContacts}
              readOnly
              rightIcon="chevron-down"
              value={inputValue}
              width="100%"
            />
          </SunshineDropdown.Trigger>

          <SunshineDropdown.List>
            <Center paddingBottom="space-16" paddingTop="space-8">
              <Button
                onClick={() => {
                  if (onClickAddOption) {
                    onClickAddOption();
                    return;
                  }

                  logEvent({
                    name: 'Add New Client Contact',
                    properties: {
                      from: 'InvoiceDetailsPage',
                      invoiceClientId: clientId,
                    },
                  });

                  setIsModalOpen(true);
                }}
                variant="secondary"
              >
                {locales.addContact}
              </Button>
            </Center>

            <Checkbox.Group onChange={onChangeSelect} value={selectValue}>
              {contactsOptions.map((contact) => (
                <Checkbox key={contact.value} value={contact.value}>
                  <Checkbox.Title>{contact.label}</Checkbox.Title>
                </Checkbox>
              ))}
            </Checkbox.Group>
          </SunshineDropdown.List>
        </SunshineDropdown>
      </Form.Field>

      <Form.Field>
        <Form.Checkbox
          id={checkboxId}
          isChecked={checked}
          onChange={(e) => onChangeCheckbox(e.target.checked)}
        >
          <Form.Checkbox.Label>{locales.ccUser}</Form.Checkbox.Label>
        </Form.Checkbox>
      </Form.Field>

      {isModalOpen && client !== undefined ? (
        <AddClientContactModal
          client={client}
          hideModal={() => setIsModalOpen(false)}
          isOpen={isModalOpen}
          selectNewContact={selectNewContact}
        />
      ) : null}
    </Stack>
  );
};

export default SelectContactsToSendTo;
